// chatContext/chatAppContextStore.ts
//
// The one channel a surface has into the companion's app context: the anchor it
// is showing, the text the student selected, and any composer prefill. It
// replaces the legacy `AIDrawerProvider.setContext` calls, which each surface
// used to fill with a different ad-hoc shape.
//
// Deliberately a module store rather than Redux or a provider: the publishers
// (a video player, the document viewer, the selection tooltip) and the consumer
// (whichever chat shell is mounted) sit on opposite sides of the tree, the
// value must not be persisted, and nothing needs to mount for it to work.

import { useEffect, useRef, useSyncExternalStore } from 'react';
import type {
  ChatActivity,
  ChatAnchorInput,
  ChatCourseContext,
  ChatViewing,
} from '../types/chatEnvelope';
import { registerChatSessionClearer } from '../slices/chat/chatSessionLifecycle';

/** Which mounted chat surface may claim a staged automatic send. */
export type ChatPendingSendTarget = 'chat_page' | 'drawer';

/**
 * A one-shot message staged by a producer while its destination surface mounts.
 *
 * `id` makes consumption atomic: a stale effect can never clear a newer
 * request that replaced the one it observed.
 */
export interface ChatPendingSend {
  id: string;
  message: string;
  target: ChatPendingSendTarget;
}

export interface ChatAppContextInputs {
  /** What the next turn is about: the surface's anchor, or the last engaged block's. */
  anchor: ChatAnchorInput | null;
  /**
   * The anchor a *surface* published — the question on the QBank page, the
   * video playing, the article open. Block claims never land here (ORA-628):
   * a served practice question the student answers mid-conversation belongs
   * in the envelope, but the thread they are in stays the same thread. This
   * is what thread resolution follows.
   */
  surfaceAnchor: ChatAnchorInput | null;
  selection: string | null;
  prefill: string | null;
  pendingSend: ChatPendingSend | null;
  /**
   * The context key (`chatContextKey`) the student removed from the chat with
   * the composer's pill (ORA-662). Detach is sticky per item: it holds for
   * every turn until the key changes — the next question, the next card, a new
   * page — which `useAppContextEnvelope` watches for and clears.
   */
  detachedAnchorKey: string | null;
  /** The side viewer only; it deliberately does not participate in thread keys. */
  viewing: ChatViewing | null;
  /**
   * Where in a course the student is (Courses V1). Context, except that a
   * published `moduleId` (the course session) makes the module the thread
   * (ORA-1055, `toSurfaceConversationAnchor`).
   */
  course: ChatCourseContext | null;
  /**
   * The study activity a course step publishes (Courses V1). The study routes
   * derive `qbank_block` and `flashcard_run` from the study slices; a course
   * step runs its questions and cards outside those slices, so it says what
   * it is doing here instead. Read only on the `course.session` route.
   */
  courseActivity: ChatActivity | null;
}

const EMPTY: ChatAppContextInputs = {
  anchor: null,
  surfaceAnchor: null,
  selection: null,
  prefill: null,
  pendingSend: null,
  detachedAnchorKey: null,
  viewing: null,
  course: null,
  courseActivity: null,
};

let state: ChatAppContextInputs = EMPTY;
let nextPendingSendId = 0;
/**
 * Which surface published the anchor now in force (ORA-458).
 *
 * Publishers are not exclusive: the document viewer's provider is mounted on
 * every protected page and the article reader is mounted inside the study
 * panel, both of them idle. Without an owner an idle publisher's `null` would
 * retract the anchor the surface the student is actually using had published,
 * so `null` publishes nothing and a surface may only ever retract its own.
 */
let anchorOwner: symbol | null = null;
/**
 * The anchor the surfaces last published, kept while a claim is in force so
 * the page's own anchor is back the moment the claim is released.
 */
let publishedAnchor: ChatAnchorInput | null = null;
/**
 * A lecture document session's claim (ORA-736): View Document makes the
 * lecture the surface anchor until the document closes, whatever the page
 * beneath it publishes meanwhile.
 */
let claimedAnchor: ChatAnchorInput | null = null;
const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((listener) => listener());
};

const setState = (next: ChatAppContextInputs) => {
  state = next;
  emit();
};

const isSameValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;

  // The document anchor's `pages` window is a fresh array on every scroll
  // update, so identity alone would call every republish a change.
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => item === b[index]);
  }

  return false;
};

/**
 * Anchors are re-published from render effects, so an unchanged anchor must not
 * produce a new object: a fresh reference would re-derive the envelope and
 * re-render every chat surface on every frame of a video.
 */
export const isSameChatAnchor = (
  a: ChatAnchorInput | null,
  b: ChatAnchorInput | null
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.type !== b.type) return false;

  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);

  return [...keys].every((key) =>
    isSameValue(
      (a as Record<string, unknown>)[key],
      (b as Record<string, unknown>)[key]
    )
  );
};

export const getChatAppContextInputs = (): ChatAppContextInputs => state;

export const getDetachedChatContextKey = (): string | null =>
  state.detachedAnchorKey;

export const subscribeChatAppContext = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/**
 * Publish the content the student is on. Surfaces own this; nothing else.
 *
 * `owner` identifies the publishing surface, so it can take its own anchor
 * back later without disturbing one another surface has since published. A
 * caller with no token (a test, a one-off) publishes anonymously and can only
 * be retracted by an unconditional clear.
 */
export const setChatAnchor = (
  anchor: ChatAnchorInput | null,
  owner: symbol | null = null
): void => {
  // Ownership is recorded even when the anchor itself has not changed: the
  // same content can be handed from one surface to another (a video opened
  // from the document viewer), and whoever published last owns it.
  anchorOwner = anchor ? owner : null;
  publishedAnchor = anchor;

  // A claim outranks every surface; what they publish waits for its release.
  if (claimedAnchor) return;

  // A re-publish of what the surface already published — the video player
  // on every `timeupdate`, the article reader on scroll, any re-render — is
  // not the surface moving, so it must not take the envelope back from a
  // block the student engaged in the meantime (ORA-628).
  if (isSameChatAnchor(state.surfaceAnchor, anchor)) return;

  setState({ ...state, anchor, surfaceAnchor: anchor });
};

/**
 * Make `anchor` the surface anchor until `releaseChatSurfaceAnchorClaim`,
 * over whatever the surfaces publish meanwhile. Thread resolution follows it
 * like any surface anchor. Re-claiming the same content with a new page
 * window updates it in place.
 */
export const claimChatSurfaceAnchor = (anchor: ChatAnchorInput): void => {
  claimedAnchor = anchor;

  if (isSameChatAnchor(state.surfaceAnchor, anchor)) return;

  setState({ ...state, anchor, surfaceAnchor: anchor });
};

/** Whether a lecture document session holds the surface anchor. */
export const isChatSurfaceAnchorClaimed = (): boolean => claimedAnchor !== null;

/** End the claim and put back the anchor the surfaces last published. */
export const releaseChatSurfaceAnchorClaim = (): void => {
  if (!claimedAnchor) return;
  claimedAnchor = null;

  if (isSameChatAnchor(state.surfaceAnchor, publishedAnchor)) return;

  setState({
    ...state,
    anchor: publishedAnchor,
    surfaceAnchor: publishedAnchor,
  });
};

/** Drop the anchor on leaving a surface. */
export const clearChatAnchor = (): void => setChatAnchor(null);

/**
 * A block the student engaged inside the thread (`useBlockAnchor`) becomes
 * what the next turn is about, without touching the surface's own anchor —
 * so nothing that follows `surfaceAnchor` (the thread the drawer is on)
 * moves because a served question was answered (ORA-628).
 */
export const setBlockChatAnchor = (anchor: ChatAnchorInput): void => {
  if (isSameChatAnchor(state.anchor, anchor)) return;

  setState({ ...state, anchor });
};

/**
 * Retract a block's claim, if it is still the one in force, and hand the
 * envelope back to whatever the surface published.
 */
export const clearBlockChatAnchor = (claimed: ChatAnchorInput | null): void => {
  if (!claimed || !isSameChatAnchor(state.anchor, claimed)) return;
  if (isSameChatAnchor(state.anchor, state.surfaceAnchor)) return;

  setState({ ...state, anchor: state.surfaceAnchor });
};

/**
 * Retract the anchor only if this owner is the one that published it. What
 * keeps an idle or unmounting surface from wiping the anchor another surface
 * put up in the meantime.
 */
export const clearChatAnchorOwnedBy = (owner: symbol): void => {
  if (anchorOwner !== owner) return;

  clearChatAnchor();
};

/**
 * Remove (`key`) or restore (`null`) what the composer's pill is showing. The
 * pill is the only caller; everything else reads it.
 */
export const setDetachedChatContextKey = (key: string | null): void => {
  if (state.detachedAnchorKey === key) return;

  setState({ ...state, detachedAnchorKey: key });
};

export const setChatSelection = (selection: string | null): void => {
  if (state.selection === selection) return;

  setState({ ...state, selection });
};

export const setChatPrefill = (prefill: string | null): void => {
  if (state.prefill === prefill) return;

  setState({ ...state, prefill });
};

const isSameViewing = (
  left: ChatViewing | null,
  right: ChatViewing | null
): boolean => {
  if (left === right) return true;
  if (!left || !right || left.kind !== right.kind) return false;

  const hasSameId =
    left.kind === 'lecture' && right.kind === 'lecture'
      ? left.lectureId === right.lectureId
      : left.kind === 'chat_document' && right.kind === 'chat_document'
        ? left.documentId === right.documentId
        : false;

  return hasSameId && isSameValue(left.pages, right.pages);
};

export const setChatViewing = (viewing: ChatViewing | null): void => {
  if (isSameViewing(state.viewing, viewing)) return;
  setState({ ...state, viewing });
};

const isSameCourseContext = (
  left: ChatCourseContext | null,
  right: ChatCourseContext | null
): boolean =>
  left === right ||
  (left !== null &&
    right !== null &&
    left.courseId === right.courseId &&
    left.moduleId === right.moduleId &&
    left.stepId === right.stepId);

export const setChatCourseContext = (
  course: ChatCourseContext | null
): void => {
  if (isSameCourseContext(state.course, course)) return;
  setState({ ...state, course });
};

const isSameActivity = (
  left: ChatActivity | null,
  right: ChatActivity | null
): boolean => {
  if (left === right) return true;
  if (!left || !right) return false;

  const leftEntries = Object.entries(left);

  return (
    leftEntries.length === Object.keys(right).length &&
    leftEntries.every(
      ([key, value]) => (right as Record<string, unknown>)[key] === value
    )
  );
};

export const setChatCourseActivity = (
  courseActivity: ChatActivity | null
): void => {
  if (isSameActivity(state.courseActivity, courseActivity)) return;
  setState({ ...state, courseActivity });
};

/**
 * Stage a message until the requested chat surface is mounted and its thread
 * is ready. Newer asks deliberately replace older unsent asks.
 */
export const setPendingChatSend = (
  message: string,
  target: ChatPendingSendTarget
): ChatPendingSend => {
  const pendingSend: ChatPendingSend = {
    id: `pending-chat-send-${++nextPendingSendId}`,
    message,
    target,
  };

  setState({ ...state, pendingSend });
  return pendingSend;
};

/**
 * Claim one exact pending request. The id check makes this a compare-and-swap:
 * an effect that saw an older request cannot consume a newer replacement.
 */
export const consumePendingChatSend = (id: string): ChatPendingSend | null => {
  if (state.pendingSend?.id !== id) return null;

  const pendingSend = state.pendingSend;
  setState({ ...state, pendingSend: null });
  return pendingSend;
};

/**
 * Drop a staged send that was never delivered: whichever one is staged, or only
 * the one matching every field given.
 *
 * A staged ask is one-shot and belongs to the moment it was made, so the
 * producers' surfaces retract it when the student walks away from it — closing
 * the drawer they asked from, or leaving the route.
 */
export const clearPendingChatSend = (match?: {
  id?: string;
  target?: ChatPendingSendTarget;
}): void => {
  const { pendingSend } = state;

  if (!pendingSend) return;
  if (match?.id !== undefined && pendingSend.id !== match.id) return;
  if (match?.target !== undefined && pendingSend.target !== match.target)
    return;

  setState({ ...state, pendingSend: null });
};

/**
 * "Ask Ora about this": the highlighted text becomes both the composer prefill
 * and the envelope's selection. Opening the shell is the shell's business.
 */
export const askOraAboutSelection = (text: string): void => {
  if (state.selection === text && state.prefill === text) return;

  setState({ ...state, selection: text, prefill: text });
};

/**
 * Drop a highlight the student removed from the composer's pill (ORA-662).
 *
 * `askOraAboutSelection` seeds the prefill from the same text, and the prefill
 * is an envelope field of its own — clearing only the selection would still
 * quote the passage to the model. The prefill goes only when it is still that
 * same text; anything the student typed over it is theirs. `ChatComposer`
 * applies a prefill only when it is non-null, so the draft in the box survives
 * either way.
 */
export const clearHighlightedChatSelection = (): void => {
  const { selection, prefill } = state;

  if (selection === null) return;

  setState({
    ...state,
    selection: null,
    prefill: prefill === selection ? null : prefill,
  });
};

/** Clear the one-shot fields once a turn has been sent from them. */
export const clearChatSelectionAndPrefill = (): void => {
  if (state.selection === null && state.prefill === null) return;

  setState({ ...state, selection: null, prefill: null });
};

/** Test helper: forget every published input. */
export const resetChatAppContext = (): void => {
  anchorOwner = null;
  publishedAnchor = null;
  claimedAnchor = null;

  if (state === EMPTY) return;

  setState(EMPTY);
};

registerChatSessionClearer(resetChatAppContext);

export const useChatAppContextInputs = (): ChatAppContextInputs =>
  useSyncExternalStore(
    subscribeChatAppContext,
    getChatAppContextInputs,
    getChatAppContextInputs
  );

/** Subscribe to the detach flag alone, for a consumer that needs nothing else. */
export const useDetachedChatContextKey = (): string | null =>
  useSyncExternalStore(
    subscribeChatAppContext,
    getDetachedChatContextKey,
    getDetachedChatContextKey
  );

/**
 * The publisher side, for a surface that shows anchored content. Use this
 * rather than calling `setChatAnchor` by hand.
 *
 * Two rules, both of them about surfaces that overlap:
 *
 * - A `null` anchor publishes nothing. Several surfaces that CAN publish are
 *   mounted at once and only one of them has something to show — the document
 *   viewer's provider sits on every protected page, the article reader sits
 *   inside the study panel — so an idle one that retracted would wipe the
 *   anchor the student is actually looking at.
 * - Retraction is owner-scoped. On unmount, or when its own content goes away,
 *   a surface takes back only what it published, so a student who leaves a
 *   video does not keep telling the companion they are watching it, and a
 *   surface that has already handed over to another one clears nothing.
 */
export const usePublishChatAnchor = (anchor: ChatAnchorInput | null): void => {
  // One token per hook instance, stable for the life of the component: it is
  // what makes retraction this surface's business and nobody else's.
  const owner = useRef<symbol>();

  if (!owner.current) owner.current = Symbol('chat-anchor-publisher');

  const token = owner.current;

  // No dependency list: callers build the anchor inline, so a fresh object
  // arrives every render and the store's own equality check does the filtering.
  useEffect(() => {
    if (anchor) {
      setChatAnchor(anchor, token);
      return;
    }

    clearChatAnchorOwnedBy(token);
  });

  useEffect(() => () => clearChatAnchorOwnedBy(token), [token]);
};

/**
 * Publish where in a course the student is, for as long as the surface is
 * mounted (Courses V1). Ids only; the server reads everything else. Cleared
 * on unmount, so leaving the course leaves no stale context behind.
 */
export const usePublishChatCourseContext = (
  course: ChatCourseContext | null
): void => {
  const courseId = course?.courseId ?? null;
  const moduleId = course?.moduleId ?? null;
  const stepId = course?.stepId ?? null;

  useEffect(() => {
    setChatCourseContext(
      courseId
        ? {
            courseId,
            ...(moduleId ? { moduleId } : {}),
            ...(stepId ? { stepId } : {}),
          }
        : null
    );
  }, [courseId, moduleId, stepId]);

  useEffect(() => () => setChatCourseContext(null), []);
};

/**
 * Publish the study activity of the course step on screen, for as long as the
 * step is mounted (Courses V1). Re-published from every render, so callers
 * build it inline; the store drops a value equal to the one in force.
 */
export const usePublishChatCourseActivity = (
  activity: ChatActivity | null
): void => {
  useEffect(() => {
    setChatCourseActivity(activity);
  });

  useEffect(() => () => setChatCourseActivity(null), []);
};
